// Dependencies
import { addSeconds, differenceInSeconds, isValid, parseISO } from "date-fns";
import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { redirect } from "react-router-dom";

// Application Dependencies
import { Avatar } from "@performyard/py-components";

import Button from "../../components/Button";
import RootModal from "../../components/Modal/RootModal";

import { useCurrentUser } from "../../api/hooks";
import { useCompanySettings } from "../../api/hooks/defs/company";

import { extendSession } from "../../api/services";

import Vault from "../../utils/Vault";

// styles
import css from "./SessionManager.module.scss";
// Internal Dependencies
import config from "./config";
import { useSessionStore } from "./sessionStore";

const WARNING_TIME = 120; // seconds
const FACTOR_OF_SAFETY = 10; // seconds. This ensures we allow the user to extend their session *before* it expires
const REDIRECT_COUNTDOWN_TIME = 300; // 5 minutes in seconds

export const SessionManager = () => {
	const userRequest = useCurrentUser({
		refetchInterval: false,
		refetchOnWindowFocus: false,
	});
	const { data: companySettings } = useCompanySettings();

	// state
	const [secondsLeft, setSecondsLeft] = useState<number>(Infinity);
	const [showWarning, setShowWarning] = useState<boolean>(false);
	const [redirectCountdown, setRedirectCountdown] = useState<number>(REDIRECT_COUNTDOWN_TIME);

	// refs
	const loginWindow = useRef<Window | null>(null);
	const currentUserRef = useRef<string | null>(null);
	const lastValidCompanyID = useRef<string | null>(null);

	// variables
	const userEmail = userRequest.data?.me?.email;
	const userId = userRequest.data?.me?.authorization?.payload?.user_id;
	const companyID = userRequest.data?.me?.authorization?.payload?.company_id;
	const isRedirectEnabled = companySettings?.authentication?.sessionTimeoutRedirect === "on";
	const isSessionExpired = secondsLeft < 1;

	// this is used to extend the user session but if the user is logged out,
	// the value stored in userRequest.data will be undefined.
	if (companyID) {
		lastValidCompanyID.current = companyID;
	}

	// Helper function to format countdown time as "Xm Xs"
	const formatCountdownTime = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}m ${remainingSeconds}s`;
	};

	// refreshes the app state to determine the current session expiration
	// time. This will be triggered on mount, when the user extends their
	// session, and when the session is extended in another tab.
	const refreshSessionState = (updateStorage: boolean) => {
		userRequest.refetch();

		// Reset secondsLeft so isSessionExpired flips back to false. The interval
		// only writes secondsLeft when timeLeft < WARNING_TIME, so without this
		// the value stays stuck after extension and downstream consumers (e.g.
		// the WebSocket reconnect gate) never see the session resume.
		setSecondsLeft(Infinity);

		// clears the modal. Whether or not the modal is open, will be
		// determined in the next tick.
		setShowWarning(false);
		// closes the login window if it is open
		if (loginWindow.current) {
			loginWindow.current.close();
			loginWindow.current = null;
		}

		// IMPORTANT!!! This is a critical check to prevent an infinite loop.
		// This short circuits the function so we don't cause an infinite loop
		// if user has other tabs open.
		if (!updateStorage) {
			return;
		}

		// Update the expiration time in Vault. This will inform all other
		// tabs that the session has been extended.
		const sessionLengthCookie = Cookies.get(config.expirationCookieName); // 12 hours
		if (sessionLengthCookie) {
			const sessionLength = parseInt(sessionLengthCookie, 10);
			const sessionExpiresAt = addSeconds(new Date(), sessionLength);

			if (isValid(sessionExpiresAt)) {
				Vault.set(config.expirationStorageName, sessionExpiresAt.toISOString());
			} else {
				const fallbackDate = addSeconds(new Date(), 43200); // 12 hours
				Vault.set(config.expirationStorageName, fallbackDate.toISOString());
			}
		}

		// Clear the redirect countdown when session is extended across all tabs
		Vault.delete(config.redirectCountdownStorageName);
		setRedirectCountdown(REDIRECT_COUNTDOWN_TIME);
	};

	// Triggered when the storage changes, this happens when another tab
	// updates expiration time.
	const handleStorageChange = () => {
		const updateStorage = false;
		refreshSessionState(updateStorage);
	};

	// Triggered when the redirect countdown storage changes
	const handleRedirectCountdownStorageChange = () => {
		// Update the redirect countdown based on the shared timestamp
		updateRedirectCountdownFromStorage();
	};

	// Updates the redirect countdown based on the shared timestamp in storage
	const updateRedirectCountdownFromStorage = () => {
		const redirectStartTimeString = Vault.get(config.redirectCountdownStorageName);
		if (!redirectStartTimeString) {
			setRedirectCountdown(REDIRECT_COUNTDOWN_TIME);
			return;
		}

		const redirectStartTime = parseISO(redirectStartTimeString);
		if (!isValid(redirectStartTime)) {
			setRedirectCountdown(REDIRECT_COUNTDOWN_TIME);
			return;
		}

		const elapsed = differenceInSeconds(new Date(), redirectStartTime);
		const remaining = Math.max(0, REDIRECT_COUNTDOWN_TIME - elapsed);
		setRedirectCountdown(remaining);
	};

	// this effect runs on mount and subscribes to changes to the expiration
	// time. It also sets up an interval to check the expiration time every
	// second.
	useEffect(() => {
		// refresh the session app state
		const updateStorage = true;
		refreshSessionState(updateStorage);

		// Subscribe to changes to the expiration time. This will allow us to
		// reset the timer if the user extends their session in another tab.
		Vault.subscribe(config.expirationStorageName, handleStorageChange);

		// Subscribe to redirect countdown changes for cross-tab coordination
		Vault.subscribe(config.redirectCountdownStorageName, handleRedirectCountdownStorageChange);

		// Set up an interval to check the expiration time every second.
		const intervalId = setInterval(() => {
			const sessionExpiresAtFromVault = parseISO(Vault.get(config.expirationStorageName));
			const timeLeft = differenceInSeconds(sessionExpiresAtFromVault, new Date());

			if (timeLeft < WARNING_TIME) {
				setShowWarning(true);
				setSecondsLeft(timeLeft - FACTOR_OF_SAFETY);
			}

			// Update redirect countdown from storage on each tick for accuracy
			updateRedirectCountdownFromStorage();
		}, 1000); // Runs every second

		// Cleanup function to clear the interval when the component unmounts
		return () => {
			clearInterval(intervalId);
			Vault.unsubscribe(config.expirationStorageName, handleStorageChange);
			Vault.unsubscribe(config.redirectCountdownStorageName, handleRedirectCountdownStorageChange);
		};
	}, []);

	useEffect(() => {
		try {
			if (currentUserRef.current === null) {
				// if the current user is null and we have a userId, set the
				// current user to the userId so we can detect changes.
				if (userId) {
					currentUserRef.current = userId;
				}

				// stop here because we know there isn't a valid user change.
				// this is most likely the initial render.
				return;
			}

			// if the user is different, we redirect to the dashboard. This
			// will happen if the user takes over another user account.
			if (currentUserRef.current !== userId) {
				redirect("/");
				return;
			}
		} catch (e) {
			// do nothing
		}
	}, [userId]);

	// Mirror expiration state into the shared zustand store so other parts of the
	// app (e.g. WebSocket hooks) can react without duplicating the timer logic.
	useEffect(() => {
		useSessionStore.getState().setIsExpired(isSessionExpired);
	}, [isSessionExpired]);

	// Handle redirect countdown when session is expired and redirect is enabled
	useEffect(() => {
		if (!isSessionExpired || !isRedirectEnabled) {
			return;
		}

		// Check if we already have a redirect start time in storage
		const existingRedirectStartTime = Vault.get(config.redirectCountdownStorageName);

		if (!existingRedirectStartTime) {
			// This is the first tab to detect session expiration, set the start time
			const redirectStartTime = new Date().toISOString();
			Vault.set(config.redirectCountdownStorageName, redirectStartTime);
		}

		// Set up interval to check if we should redirect
		const redirectInterval = setInterval(() => {
			const redirectStartTimeString = Vault.get(config.redirectCountdownStorageName);
			if (!redirectStartTimeString) {
				return;
			}

			const redirectStartTime = parseISO(redirectStartTimeString);
			if (!isValid(redirectStartTime)) {
				return;
			}

			const elapsed = differenceInSeconds(new Date(), redirectStartTime);

			if (elapsed >= REDIRECT_COUNTDOWN_TIME) {
				// Clear the interval to prevent multiple redirects
				clearInterval(redirectInterval);
				// Redirect to logout with company ID preserved in redirect parameter
				const loginUrl = `/login?companyID=${lastValidCompanyID.current}`;
				window.location.href = `/logout?redirect=${encodeURIComponent(loginUrl)}`;
			}
		}, 1000);

		return () => {
			clearInterval(redirectInterval);
		};
	}, [isSessionExpired, isRedirectEnabled]);

	// The user wants to extend their session but they have already been logged
	// out. This will open a new window to log them back in. The window will be
	// closed after the session is refreshed.
	const openSignInWindow = () => {
		const windowOptions = {
			toolbar: "no",
			location: "no",
			status: "no",
			menubar: "no",
			scrollbars: "yes",
			width: 560,
			height: 800,
		};

		loginWindow.current = window.open(
			`/login?session=true&companyID=${lastValidCompanyID.current}`,
			"targetWindow",
			Object.keys(windowOptions)
				.map((d) => `${d}=${windowOptions[d]}`)
				.join(",")
		);
	};

	// Sends a request that will update the user's cookie and restart the
	// session timer.
	const handleExtendSession = async () => {
		await extendSession();
		const updateStorage = true;
		refreshSessionState(updateStorage);
	};

	if (!showWarning) {
		return null;
	}

	return (
		<RootModal>
			<div className={css.main}>
				<div className={css.modalWrapper}>
					<div className={css.modal}>
						<div className={css.avatarContainer}>
							<div className={css.avatarWrapper}>
								<Avatar
									size={48}
									dangerouslySetClassName={"size-[60px]"}
									id={userId}
									alt={`${userEmail}'s avatar`}
								/>
							</div>
						</div>

						<div className={css.modalContent}>
							<div className={css.innerContent}>
								{secondsLeft < 1 ? (
									<div className={css.hasExpired}>
										<div className={css.email}>{userEmail}</div>
										<div className={css.loggedOutText}>
											{isRedirectEnabled ? (
												<>
													Due to inactivity, your PerformYard session has timed out, and you
													have been logged out of PerformYard. You will be redirected to our
													login page in {formatCountdownTime(redirectCountdown)} unless you
													extend your session to continue working.
												</>
											) : (
												<>
													Due to inactivity, your PerformYard session has timed out, and you
													have been logged out of PerformYard.{" "}
													<span className={css.emph}>
														Please extend your session to continue working
													</span>
													.
												</>
											)}
										</div>
										<div className={css.buttonContainer}>
											<Button className={css.extendSessionButton} onClick={openSignInWindow}>
												Extend Session
											</Button>
										</div>
									</div>
								) : (
									<div className={css.aboutToExpire}>
										<div className={css.title}>Session Expiration</div>
										<div className={css.countdownDescription}>
											Your session is about to expire in{" "}
											<span className={css.timeLeft}>{secondsLeft}</span> seconds. Would you like
											to extend your session?
										</div>
										<div className={css.buttonActions}>
											<Button
												isRequesting={false}
												onClick={handleExtendSession}
												className={css.action}
												color="green"
											>
												Yes, keep me signed in
											</Button>
											<Button href="/logout" className={css.action} isHollow={true}>
												Logout
											</Button>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</RootModal>
	);
};
